/**
 * XP formula constants and calculation functions for Idle Depths.
 *
 * Uses a multi-phase polynomial formula targeting:
 * - ~400 hours baseline to level 100
 * - ~200 hours optimized (with tools, potions, etc.)
 * - ~60%+ of XP required in levels 91-100
 * - Extensible to level 120 for future expansions
 *
 * @see docs/design/systems/xp-and-leveling.md
 */

// ============================================================================
// Configuration Constants
// ============================================================================

/**
 * XP formula constants for multi-phase polynomial curve.
 */
export const XP_FORMULA_CONSTANTS = {
  // Phase 1: Early Game (1-40)
  PHASE_1_MAX: 40,
  PHASE_1_CUBIC: 0.058,
  PHASE_1_QUADRATIC: 2.5,
  PHASE_1_LINEAR: 29,

  // Phase 2: Mid Game (41-75)
  PHASE_2_MAX: 75,
  PHASE_2_CUBIC: 0.15,
  PHASE_2_QUADRATIC: 1.35,
  PHASE_2_LINEAR: 21,

  // Phase 3: Late Game (76-90)
  PHASE_3_MAX: 90,
  PHASE_3_CUBIC: 0.268,
  PHASE_3_QUADRATIC: 0.85,

  // Phase 4: Prestige (91-100)
  PHASE_4_MAX: 100,
  PHASE_4_QUARTIC: 0.00415,
  PHASE_4_CUBIC: 0.31,

  // Phase 5: Future (101-120)
  PHASE_5_MAX: 120,
  PHASE_5_QUINTIC: 0.004,
  PHASE_5_QUARTIC: 0.045,

  // Level caps
  CURRENT_LEVEL_CAP: 100,
  FUTURE_LEVEL_CAP: 120,
} as const;

/**
 * Baseline XP earned per hour (for time estimates).
 */
export const BASELINE_XP_PER_HOUR = 25000;

/**
 * Optimization multiplier (tools, potions, etc. can roughly double XP rate).
 */
export const OPTIMIZATION_MULTIPLIER = 2.0;

/**
 * Maximum affinity completions for an action (100% mastery).
 */
export const MAX_AFFINITY_COMPLETIONS = 1000;

// ============================================================================
// XP Calculation Functions
// ============================================================================

/**
 * Get XP required to advance from level to level+1.
 *
 * Uses multi-phase polynomial formula:
 * - Phase 1 (1-40): Cubic + Quadratic + Linear
 * - Phase 2 (41-75): Higher Cubic
 * - Phase 3 (76-90): Steeper Cubic
 * - Phase 4 (91-100): Quartic (extreme grind)
 * - Phase 5 (101-120): Quintic (future expansion)
 *
 * @param level - The level to calculate XP for
 * @returns XP required to advance from this level
 */
export function getXPForLevel(level: number): number {
  if (level <= 1) return 0;
  if (level > XP_FORMULA_CONSTANTS.FUTURE_LEVEL_CAP) {
    return getXPForLevel(XP_FORMULA_CONSTANTS.FUTURE_LEVEL_CAP);
  }

  const { PHASE_1_MAX, PHASE_2_MAX, PHASE_3_MAX, PHASE_4_MAX } = XP_FORMULA_CONSTANTS;

  // Phase 1: Early Game (1-40)
  if (level <= PHASE_1_MAX) {
    const cubic = XP_FORMULA_CONSTANTS.PHASE_1_CUBIC * Math.pow(level, 3);
    const quadratic = XP_FORMULA_CONSTANTS.PHASE_1_QUADRATIC * Math.pow(level, 2);
    const linear = XP_FORMULA_CONSTANTS.PHASE_1_LINEAR * level;
    return Math.floor(cubic + quadratic + linear);
  }

  // Phase 2: Mid Game (41-75)
  if (level <= PHASE_2_MAX) {
    const cubic = XP_FORMULA_CONSTANTS.PHASE_2_CUBIC * Math.pow(level, 3);
    const quadratic = XP_FORMULA_CONSTANTS.PHASE_2_QUADRATIC * Math.pow(level, 2);
    const linear = XP_FORMULA_CONSTANTS.PHASE_2_LINEAR * level;
    return Math.floor(cubic + quadratic + linear);
  }

  // Phase 3: Late Game (76-90)
  if (level <= PHASE_3_MAX) {
    const cubic = XP_FORMULA_CONSTANTS.PHASE_3_CUBIC * Math.pow(level, 3);
    const quadratic = XP_FORMULA_CONSTANTS.PHASE_3_QUADRATIC * Math.pow(level, 2);
    return Math.floor(cubic + quadratic);
  }

  // Phase 4: Prestige (91-100)
  if (level <= PHASE_4_MAX) {
    const quartic = XP_FORMULA_CONSTANTS.PHASE_4_QUARTIC * Math.pow(level, 4);
    const cubic = XP_FORMULA_CONSTANTS.PHASE_4_CUBIC * Math.pow(level, 3);
    return Math.floor(quartic + cubic);
  }

  // Phase 5: Future (101-120)
  const quintic = XP_FORMULA_CONSTANTS.PHASE_5_QUINTIC * Math.pow(level, 5);
  const quartic = XP_FORMULA_CONSTANTS.PHASE_5_QUARTIC * Math.pow(level, 4);
  return Math.floor(quintic + quartic);
}

/**
 * Pre-calculated XP table for O(1) lookups.
 *
 * Index = level, value = total XP required to reach that level.
 * Generated at module load for performance.
 */
export const XP_TABLE: readonly number[] = (() => {
  const table: number[] = [0]; // Index 0 unused (levels are 1-based)

  for (let level = 1; level <= XP_FORMULA_CONSTANTS.FUTURE_LEVEL_CAP; level++) {
    const previousTotal = table[level - 1] ?? 0;
    table[level] = previousTotal + getXPForLevel(level);
  }

  return Object.freeze(table);
})();

/**
 * Get total XP required to reach a level from level 1.
 *
 * Uses pre-calculated lookup table for O(1) performance.
 *
 * @param level - The target level (1-120)
 * @returns Total XP required to reach this level
 */
export function getTotalXPForLevel(level: number): number {
  if (level <= 1) return 0;
  if (level > XP_FORMULA_CONSTANTS.FUTURE_LEVEL_CAP) {
    return XP_TABLE[XP_FORMULA_CONSTANTS.FUTURE_LEVEL_CAP] ?? 0;
  }
  return XP_TABLE[level] ?? 0;
}

/**
 * Calculate current level from total XP using binary search.
 *
 * @param totalXP - Total XP earned
 * @returns Current level (1-120)
 */
export function getLevelFromXP(totalXP: number): number {
  if (totalXP <= 0) return 1;

  // Binary search through XP table
  let low = 1;
  let high = XP_FORMULA_CONSTANTS.FUTURE_LEVEL_CAP;

  while (low < high) {
    const mid = Math.floor((low + high + 1) / 2);
    if (getTotalXPForLevel(mid) <= totalXP) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  return low;
}

/**
 * Get progress to next level as a value from 0 to 1.
 *
 * @param totalXP - Total XP earned
 * @returns Progress percentage (0-1)
 */
export function getProgressToNextLevel(totalXP: number): number {
  const currentLevel = getLevelFromXP(totalXP);

  // At max level, always full progress
  if (currentLevel >= XP_FORMULA_CONSTANTS.FUTURE_LEVEL_CAP) {
    return 1;
  }

  const xpForCurrentLevel = getTotalXPForLevel(currentLevel);
  const xpForNextLevel = getTotalXPForLevel(currentLevel + 1);
  const xpInCurrentLevel = totalXP - xpForCurrentLevel;
  const xpNeededForLevel = xpForNextLevel - xpForCurrentLevel;

  if (xpNeededForLevel <= 0) return 1;

  return Math.min(1, Math.max(0, xpInCurrentLevel / xpNeededForLevel));
}

/**
 * Get XP remaining until next level.
 *
 * @param totalXP - Total XP earned
 * @returns XP needed to reach next level
 */
export function getXPToNextLevel(totalXP: number): number {
  const currentLevel = getLevelFromXP(totalXP);

  // At max level, no more XP needed
  if (currentLevel >= XP_FORMULA_CONSTANTS.FUTURE_LEVEL_CAP) {
    return 0;
  }

  const xpForNextLevel = getTotalXPForLevel(currentLevel + 1);
  return Math.max(0, xpForNextLevel - totalXP);
}

/**
 * Calculate affinity percentage from completions.
 *
 * @param completions - Number of action completions
 * @returns Affinity percentage (0-100)
 */
export function calculateAffinityPercent(completions: number): number {
  return Math.min(100, (completions / MAX_AFFINITY_COMPLETIONS) * 100);
}

/**
 * Check if an action is mastered (100% affinity).
 *
 * @param completions - Number of action completions
 * @returns True if mastered
 */
export function isActionMastered(completions: number): boolean {
  return completions >= MAX_AFFINITY_COMPLETIONS;
}
